import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Separator } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import { useGetQueueDashboard } from 'actions/support-queue';
import { getRestAPIGetErrorMessage } from 'utils';
import FilterBar from './components/filter-bar';
import Tiles from './components/tiles';
import TicketRows, { EmptyPane } from './components/ticket-rows';
import QueueModal from './components/queue-modal';
import {
  CrossLink,
  Message,
  Pane,
  PaneGrid,
  Count,
  OpenAll,
  PaneHead,
  PaneTitle,
  RetryButton,
  RowList,
  Screen,
  ScreenTitle,
  SortNote,
  StaleBanner,
  TitleRow,
} from './styled-comps';
import {
  HOT_PANE,
  MAX_POLL_FAILURES,
  MODAL_KIND,
  PANES,
  POLL_INTERVAL_MS,
  QM,
} from './constants';
import {
  fmtClock,
  fmtDateTimeLocal,
  fmtElapsed,
  istInputToEpoch,
  startOfIstToday,
} from './utils';

const nowSeconds = () => Math.floor(Date.now() / 1000);

const SupportQueue = () => {
  const [region, setRegion] = useState(null);
  // The window's start is pinned at load and its end tracks now. Recomputing
  // the start at midnight would empty the board halfway through the US night
  // shift; freezing the end would mean new tickets never appear at all.
  // Applied window — the only thing the query reads.
  const [fromRaw, setFromRaw] = useState(startOfIstToday);
  const [toRaw, setToRaw] = useState(null);
  // What the inputs edit. A `datetime-local` fires a change per segment, so
  // binding the query to them directly issued a request per keystroke —
  // several for half-typed dates that were never a window anyone asked for.
  const [draftFrom, setDraftFrom] = useState(fromRaw);
  const [draftTo, setDraftTo] = useState(toRaw);
  const [windowOpen, setWindowOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const [tick, setTick] = useState(0);
  const [clock, setClock] = useState(nowSeconds);
  const [lastOk, setLastOk] = useState(null);
  // The hook drops `data` on a failed request, so the last good snapshot is
  // held here. Without it a single failed poll blanks the whole screen — and
  // an empty dashboard reads as "nothing is wrong", which is the worst
  // possible lie for this surface.
  const [snapshot, setSnapshot] = useState(null);
  const [fails, setFails] = useState(0);
  const paneRefs = useRef({});
  const [flashed, setFlashed] = useState(null);
  const flashTimer = useRef(null);

  const stopped = fails >= MAX_POLL_FAILURES;

  const query = useMemo(() => {
    const q = {};
    if (region) q.region = region;
    const from = istInputToEpoch(fromRaw);
    if (from !== null) q.created_from = from;
    const to = toRaw ? istInputToEpoch(toRaw) : null;
    if (to !== null) q.created_to = to;
    return q;
  }, [region, fromRaw, toRaw]);

  const {
    data,
    get,
    resource,
    isProcessing,
    isProcessed,
    isProcessingFailed,
  } = useGetQueueDashboard();
  // NOT the hook's `error`: it returns `resource.error`, a key nothing ever
  // writes — the failure is filed under `__ERROR__`. Read through the shared
  // helper, so the two messages that actually reach a user reach them intact:
  // the 403 from the access gatekeeper and the 400 on an inverted window.
  const errorMessage =
    getRestAPIGetErrorMessage(resource) || 'the queue service did not respond';

  const fetchNow = useCallback(() => {
    get('queue', query);
  }, [get, query]);

  // Refetch whenever the filters change, and whenever the poll ticks.
  useEffect(() => {
    fetchNow();
  }, [fetchNow, tick]);

  useEffect(() => {
    if (isProcessingFailed) {
      setFails(f => f + 1);
      return;
    }
    if (data) {
      setFails(0);
      setLastOk(nowSeconds());
      setSnapshot(data);
    }
  }, [data, isProcessingFailed]);

  // A second hand for the elapsed badges and the "updated Ns ago" chip, so
  // the numbers keep moving between polls instead of freezing.
  // Paused with the tab, like the poll loop: this drives elapsed badges
  // nobody is reading while hidden.
  useEffect(() => {
    let id = null;
    const start = () => {
      if (id === null) id = setInterval(() => setClock(nowSeconds()), 1000);
    };
    const stop = () => {
      if (id !== null) {
        clearInterval(id);
        id = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        setClock(nowSeconds());
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Poll on a timer, paused while the tab is hidden — this screen lives on a
  // second monitor all shift and polling a hidden tab burns queries for
  // nobody. Coming back refetches immediately rather than waiting out the
  // interval.
  useEffect(() => {
    if (stopped) return undefined;
    let id = null;
    const start = () => {
      if (id === null) id = setInterval(() => setTick(t => t + 1), POLL_INTERVAL_MS);
    };
    const stop = () => {
      if (id !== null) {
        clearInterval(id);
        id = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        setTick(t => t + 1);
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [stopped]);

  // A preset is one click with nothing half-typed to protect, so it applies
  // on the spot; only the typed fields wait for Apply.
  const onPreset = useCallback(hours => {
    const now = new Date();
    const from =
      hours === null
        ? startOfIstToday()
        : fmtDateTimeLocal(new Date(now.getTime() - hours * 3600000));
    setFromRaw(from);
    setDraftFrom(from);
    setToRaw(null);
    setDraftTo(null);
    setWindowOpen(false);
  }, []);

  // Differing is not enough — it has to parse. `query` omits `created_from`
  // when `istInputToEpoch` rejects the value, and an omitted lower bound is
  // not a narrower window but an unbounded one: committing a half-typed year
  // like `0202-09-22` would quietly ask for every ticket ever created, the
  // scan `MIN_YEAR` exists to prevent. Enter makes this reachable mid-edit.
  const draftFromValid = istInputToEpoch(draftFrom) !== null;
  const draftToValid = !draftTo || istInputToEpoch(draftTo) !== null;
  const canApply =
    draftFromValid &&
    draftToValid &&
    (draftFrom !== fromRaw || (draftTo || null) !== (toRaw || null));

  const onApply = useCallback(() => {
    if (!canApply) return;
    setFromRaw(draftFrom);
    setToRaw(draftTo || null);
    setWindowOpen(false);
  }, [canApply, draftFrom, draftTo]);

  // Reopening discards an abandoned edit rather than resurrecting it.
  const onToggleWindow = useCallback(
    open => {
      if (open) {
        setDraftFrom(fromRaw);
        setDraftTo(toRaw);
      }
      setWindowOpen(open);
    },
    [fromRaw, toRaw],
  );

  const retry = useCallback(() => {
    setFails(0);
    setTick(t => t + 1);
  }, []);

  // Scrolling alone leaves you guessing which of three near-identical
  // panes you landed on, especially when they have wrapped onto two rows.
  const jumpToPane = useCallback(key => {
    paneRefs.current[key]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setFlashed(key);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlashed(null), 2000);
  }, []);

  useEffect(() => () => clearTimeout(flashTimer.current), []);

  const shown = data || snapshot;
  const tiles = shown?.tiles;
  const panes = shown?.panes || {};
  const totals = shown?.totals || {};
  const showRegion = region === null;

  // Only reachable before the first successful load — once a snapshot exists
  // it stays on screen through any number of failures, marked stale by the
  // banner rather than replaced.
  // `isProcessed && !tiles` is a 200 that carried no board — a shape change
  // or a half-rolled-out gateway. Without it the screen sits on "Loading…"
  // forever with no Retry, while the poll loop keeps firing silently.
  const cannotLoad = isProcessingFailed || (isProcessed && !shown?.tiles);

  if (!tiles) {
    return (
      <Screen>
        <Message>
          <Text variant="body1">
            {cannotLoad
              ? `Could not load the queue — ${errorMessage}`
              : 'Loading the queue…'}
          </Text>
          {cannotLoad && (
            <RetryButton type="button" onClick={retry}>
              Retry
            </RetryButton>
          )}
        </Message>
      </Screen>
    );
  }

  return (
    <Screen>
      <TitleRow>
        <ScreenTitle>Parent support · live queue</ScreenTitle>
        <CrossLink href="/support-agent" target="_blank" rel="noopener noreferrer">
          Agent inbox ↗
        </CrossLink>
      </TitleRow>

      <FilterBar
        region={region}
        onRegionChange={setRegion}
        fromRaw={fromRaw}
        toRaw={toRaw}
        draftFrom={draftFrom}
        draftTo={draftTo}
        nowRaw={fmtDateTimeLocal(new Date(clock * 1000))}
        onFromChange={setDraftFrom}
        onToChange={setDraftTo}
        onApply={onApply}
        canApply={canApply}
        onPreset={onPreset}
        windowOpen={windowOpen}
        onToggleWindow={onToggleWindow}
        secondsSinceRefresh={lastOk ? clock - lastOk : 0}
        stopped={stopped}
      />

      {stopped && (
        <StaleBanner>
          <strong>Refresh stopped</strong>
          <span>
            {MAX_POLL_FAILURES} consecutive polls failed — {errorMessage}. Nothing below
            has updated since.
          </span>
          <span>
            Data as of {lastOk ? fmtClock(new Date(lastOk * 1000)) : '—'}
            {lastOk ? ` · ${fmtElapsed(clock - lastOk)} old` : ''}
          </span>
          <RetryButton type="button" onClick={retry}>
            Retry now
          </RetryButton>
        </StaleBanner>
      )}

      <Tiles tiles={tiles} onJumpToPane={jumpToPane} onOpenModal={setModal} />

      <PaneGrid>
        {PANES.map(pane => {
          const rows = panes[pane.key] || [];
          const total = totals[pane.key] ?? rows.length;
          const hot = pane.key === HOT_PANE && total > 0;
          return (
            <Pane
              key={pane.key}
              $flash={flashed === pane.key}
              ref={el => {
                paneRefs.current[pane.key] = el;
              }}
            >
              <PaneHead>
                <PaneTitle>{pane.title}</PaneTitle>
                <Count $hot={hot}>{total}</Count>
                {rows.length < total ? (
                  <OpenAll
                    type="button"
                    onClick={() => setModal({ kind: MODAL_KIND.PANE, pane: pane.key })}
                  >
                    showing {rows.length} of {total} · open all ↗
                  </OpenAll>
                ) : (
                  <SortNote>most at risk first</SortNote>
                )}
              </PaneHead>
              <RowList>
                {rows.length === 0 ? (
                  <EmptyPane title={pane.emptyTitle} note={pane.emptyNote} />
                ) : (
                  <TicketRows
                    banded
                    capped={rows.length < total}
                    rows={rows}
                    nowSeconds={clock}
                    showRegion={showRegion}
                    showOwner={pane.showOwner}
                  />
                )}
              </RowList>
            </Pane>
          );
        })}
      </PaneGrid>

      {modal && (
        <QueueModal
          kind={modal.kind}
          bucket={modal.bucket}
          pane={modal.pane}
          query={query}
          showRegion={showRegion}
          nowSeconds={clock}
          onClose={() => setModal(null)}
        />
      )}

      <Separator height={8} transparent />
      <Text variant="caption" color={QM.TEXT_TERTIARY}>
        Parents and unlinked email tickets · chat, internal and email · times in IST
        {isProcessing ? ' · refreshing…' : ''}
      </Text>
    </Screen>
  );
};

export default SupportQueue;
